import { lazy } from "react";

const OrgForm = lazy(() => import("../Setup/Org/OrgForm"));
const Outlets = lazy(() => import("../Setup/Outlet/Outlets"));
const AddEditOutlet = lazy(() => import("../Setup/Outlet/AddEditOutlet"));
const Registers = lazy(() =>
  import("../Setup/Outlet/Register/Registers")
);
const PaymentTypes = lazy(() =>
  import("../Setup/PaymentType/PaymentTypes")
);
const Taxes = lazy(() => import("../Setup/Tax/Taxes"));
const Cities = lazy(() => import("../Setup/City/Cities"));
const VehicleBrands = lazy(() => import("../Setup/VehicleBrand/VehicleBrands"));
const ServiceBays = lazy(() => import("../Setup/ServiceBay/ServiceBays"));
const ServiceDiscountTypes = lazy(() => import("../Setup/ServiceDiscountType/ServiceDiscountTypes"));
const WeighingScaleSetup = lazy(() => import("../Setup/ScaleBarcode/WeighingScaleSetup"));
const BusinessHours = lazy(() => import("../Setup/BusinessHours/BusinessHours"));
const MaintenanceWindow = lazy(() => import("../Setup/Platform/MaintenanceWindow"));
const Employees = lazy(() => import("../CarService/Employee/EmployeeList/Employees"));
const AddEditEmployee = lazy(() => import("../CarService/Employee/Form/AddEditEmployee"));
const ViewEmp = lazy(() => import("../CarService/Employee/ViewEmployee/ViewEmp"));
const Users = lazy(() => import("../Setup/User/Users"));
const AddEditUser = lazy(() => import("../Setup/User/Form/AddEditUser"));
const Companies = lazy(() =>
  import("../Setup/Company/Companies/Companies")
);
const AddEditCompany = lazy(() =>
  import("../Setup/Company/AddEditForm/AddEditCompany")
);
const Ledgers = lazy(() =>
  import("../Setup/Company/Ledgers/Ledgers/Ledgers")
);
const AddEditLedger = lazy(() =>
  import("../Setup/Company/Ledgers/AddEditForm/AddEditLedger")
);
const Currencies = lazy(() =>
  import("../Setup/Currency").then((m) => ({ default: m.Currencies }))
);
const AccessManagement = lazy(() => import("../Setup/UserRole/AccessManagement"));
const AccessProfileForm = lazy(() => import("../Setup/UserRole/AccessProfileForm"));
const SyncInventory = lazy(() =>
  import("../Setup/SyncInventory/SyncInventory")
);
const MailSettings = lazy(() => import("../Setup/Platform/MailSettings"));
const SecuritySettings = lazy(() => import("../Setup/Platform/SecuritySettings"));
const DesktopApp = lazy(() => import("../Setup/DesktopApp/DesktopApp"));

export const setupRoutes = (access, settings) => {
  if (!settings?.fiscalYearId) return [];

  const routes = [];

  if (access?.companyAccess) {
    routes.push(
      { path: "/companies", element: Companies, title: "companies" },
      { path: "/company_form", element: AddEditCompany, title: "addCompany" },
      { path: "/company_form/:id", element: AddEditCompany, title: "editCompany" },
      { path: "/company/ledgers/:id", element: Ledgers, title: "companyLedgers" },
      { path: "/ledger_form", element: AddEditLedger, title: "addLedger" },
      { path: "/ledger_form/:id", element: AddEditLedger, title: "editLedger" },
      { path: "/sync_inventory", element: SyncInventory, title: "syncInventory" },
    );
  }

  if (access?.generalAccess) {
    routes.push({ path: "/org_info", element: OrgForm, title: "organizationInfo" });
  }

  if (access?.outletAccess) {
    routes.push({ path: "/outlets", element: Outlets, title: "outlets" });
    if (access?.outlet?.register) {
      routes.push({ path: "/outlet/registers/:id", element: Registers, title: "registers" });
    }
    if (access.outlet?.edit) {
      routes.push({ path: "/outlet_form/:id", element: AddEditOutlet, title: "editOutlet" });
    }
    if (access.outlet?.create) {
      routes.push({ path: "/outlet_form", element: AddEditOutlet, title: "addOutlet" });
    }
  }

  if (access?.paymentTypeAccess) {
    routes.push({ path: "/payment_types", element: PaymentTypes, title: "paymentTypes" });
  }
  if (access?.currencyAccess) {
    routes.push({ path: "/currencies", element: Currencies, title: "currencies" });
  }
  if (access?.taxRateAccess) {
    routes.push({ path: "/taxes", element: Taxes, title: "taxes" });
  }
  if (access?.cityAccess) {
    routes.push({ path: "/cities", element: Cities, title: "cities" });
  }
  if (settings?.businessType === 1 && access?.vehicleBrandAccess) {
    routes.push({ path: "/vehicle_brands", element: VehicleBrands, title: "vehicleBrands" });
  }
  if (settings?.businessType === 1 && access?.serviceBayAccess) {
    routes.push({ path: "/service_bays", element: ServiceBays, title: "serviceBays" });
  }
  if (access?.serviceDiscountTypeAccess) {
    routes.push({ path: "/service_discount_types", element: ServiceDiscountTypes, title: "serviceDiscountTypes" });
  }
  if (access?.businessHoursAccess) {
    routes.push({ path: "/business_hours", element: BusinessHours, title: "businessHours" });
  }

  if (access?.scaleBarcodeAccess) {
    routes.push({ path: "/weighing_scale", element: WeighingScaleSetup, title: "weighingScaleSetup" });
  }
  if (access?.employeeAccess) {
    routes.push(
      { path: "/employees", element: Employees, title: "employees" },
      { path: "/employee_form", element: AddEditEmployee, title: "addEmployee", enabled: access.employeeAccessNav?.create },
      { path: "/employee_form/:id", element: AddEditEmployee, title: "editEmployee", enabled: access.employeeAccessNav?.edit },
      { path: "/view_employee/:id", element: ViewEmp, title: "viewEmployee" },
    );
  }

  // Your OWN account, available to every signed-in user regardless of permissions.
  //
  // This used to sit inside the userAccess block behind an ownAccountEdit flag, so a
  // cashier with no user-management rights had no route at all — while the "Edit
  // Profile" button in the profile drawer navigated here unconditionally, landing them
  // nowhere. It also gated the Security tab, which would have made two-step
  // verification unreachable for exactly the staff most likely to need it.
  //
  // Safe to open because UserRepo.Put now ignores every authority field on a self-edit
  // (access profiles, row scope, company/outlet permissions, account tier) — editing
  // yourself can change your name, contact details, language and password, nothing
  // that grants access.
  routes.push({
    path: "/edit_my_account/:id",
    element: AddEditUser,
    title: "editMyAccount",
    props: { fromMyAcc: true },
  });

  if (access?.userAccess) {
    routes.push({ path: "/users", element: Users, title: "users" });
    if (access?.user?.edit) {
      routes.push({ path: "/user_form/:id", element: AddEditUser, title: "editUser" });
    }
    if (access?.user?.create) {
      routes.push({ path: "/user_form", element: AddEditUser, title: "addUser" });
    }
  }

  // Roles & Permissions is a platform-owner tool — System-Admin (tier 2) and above
  // may reach it, so direct-URL access is denied for everyone else too.
  if (Number(settings?.userType) >= 2) {
    // One master "Permission / Access" screen with the three composable catalogs
    // (CRUD / Report / Privilege) as tabs. /user_roles kept for back-compat links.
    routes.push(
      { path: "/roles_permissions", element: AccessManagement, title: "rolesPermissions" },
      { path: "/roles_permissions/:tab", element: AccessManagement, title: "rolesPermissions" },
      { path: "/user_roles", element: AccessManagement, title: "rolesPermissions" },
      // Dedicated full-page create/edit form for an access profile.
      { path: "/access_profile/:type", element: AccessProfileForm, title: "rolesPermissions" },
      { path: "/access_profile/:type/:id", element: AccessProfileForm, title: "rolesPermissions" },
    );
  }

  // SMTP transport and two-factor administration belong to the System Engineer
  // (tier 3) alone — a System Admin is deliberately excluded, so this is an exact
  // match, not >=. The server enforces the same rule; this only hides the door.
  if (Number(settings?.userType) === 3) {
    routes.push(
      { path: "/platform_email", element: MailSettings, title: "mailSettings" },
      { path: "/platform_security", element: SecuritySettings, title: "securitySettings" },
      // The desktop-app screen. Same tier as the two above and for the same reason:
      // connecting a machine creates a credential, and that is not a client-side
      // permission. The server enforces it; this only hides the door.
      { path: "/desktop_app", element: DesktopApp, title: "desktopApp" },
      // "Can I restart this server now?" Same tier and the same reason as the two
      // above: it answers when nobody is watching the till, which is the platform
      // owner's business and not the client's.
      { path: "/maintenance_window", element: MaintenanceWindow, title: "maintenanceWindow" },
    );
  }

  return routes;
};
